<div class="flex flex-col gap-6">

    <header>
        <h2 class="text-xl font-semibold text-p-text m-0">
            {{ stepTitleKey() | translate }}
        </h2>
        <p class="text-sm text-p-text-muted m-0 mt-1">
            {{ stepSubtitleKey() | translate }}
        </p>
    </header>

    <!-- Selection tiles -->
    @if (!selectedPerson()) {
        <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-3"
            role="group"
            [attr.aria-label]="('bookingV2.person.tilesAria' | translate)">
            <button
                type="button"
                class="group flex flex-col items-center text-center gap-2 p-5 rounded-lg border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                       bg-p-content-bg border-p-border text-p-text
                       hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                       aria-pressed:border-p-primary aria-pressed:bg-[color-mix(in_srgb,var(--p-primary-color)_8%,transparent)]"
                [attr.aria-pressed]="personMode() === 'search'"
                (click)="selectPersonMode('search')">
                <span
                    class="material-icons text-3xl transition-colors text-p-text-muted
                           group-hover:text-p-primary group-aria-pressed:text-p-primary"
                    aria-hidden="true">search</span>
                <span class="font-semibold text-sm text-p-text">
                    {{ 'bookingV2.person.tiles.search.title' | translate }}
                </span>
                <span class="text-xs text-p-text-muted">
                    {{ 'bookingV2.person.tiles.search.desc' | translate }}
                </span>
            </button>
            <button
                type="button"
                class="group flex flex-col items-center text-center gap-2 p-5 rounded-lg border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                       bg-p-content-bg border-p-border text-p-text
                       hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                       aria-pressed:border-p-primary aria-pressed:bg-[color-mix(in_srgb,var(--p-primary-color)_8%,transparent)]"
                [attr.aria-pressed]="personMode() === 'new'"
                (click)="selectPersonMode('new')">
                <span
                    class="material-icons text-3xl transition-colors text-p-text-muted
                           group-hover:text-p-primary group-aria-pressed:text-p-primary"
                    aria-hidden="true">add</span>
                <span class="font-semibold text-sm text-p-text">
                    {{ 'bookingV2.person.tiles.new.title' | translate }}
                </span>
                <span class="text-xs text-p-text-muted">
                    {{ 'bookingV2.person.tiles.new.desc' | translate }}
                </span>
            </button>
        </div>
    }

    <!-- Search existing person/company -->
    @if (personMode() === 'search' && !selectedPerson()) {
        <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

            <div class="flex items-center justify-between gap-3">
                <h3 class="text-base font-semibold text-p-text m-0">
                    {{ 'bookingV2.person.search.title' | translate }}
                </h3>
                <button
                    type="button"
                    class="text-p-text-muted hover:text-p-text rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                    [attr.aria-label]="('bookingV2.person.search.closeAria' | translate)"
                    (click)="closePersonPanel()">
                    <span class="material-icons" aria-hidden="true">close</span>
                </button>
            </div>

            <div class="flex gap-2 items-center w-full">
                <p-iconfield iconPosition="left" styleClass="flex-1 min-w-0">
                    <p-inputicon>
                        <span class="material-icons text-p-text-muted">search</span>
                    </p-inputicon>
                    <input
                        pInputText
                        type="search"
                        class="w-full"
                        [ngModel]="personQuery()"
                        (ngModelChange)="onPersonQueryChange($event)"
                        [placeholder]="('bookingV2.person.search.placeholder' | translate)"
                        [attr.aria-label]="('bookingV2.person.search.placeholder' | translate)" />
                </p-iconfield>
                <u2b-icon-button
                    icon="qr_code_scanner"
                    [tooltip]="('bookingV2.person.search.scanQrAria' | translate)"
                    tooltipPosition="top"
                    [ariaLabel]="('bookingV2.person.search.scanQrAria' | translate)"
                    [disabled]="importingPersonFromQr() || loadingPersonSearch()"
                    (clicked)="scanPersonFromQr()" />
            </div>

            <p class="text-xs text-p-text-muted m-0">
                {{ 'bookingV2.person.search.up2boatHint' | translate }}
            </p>

            @if (personQueryEmpty()) {
                <p class="text-xs text-p-text-muted m-0">
                    {{ 'bookingV2.person.search.minLengthHint' | translate }}
                </p>
            }

            @if (loadingPersonSearch()) {
                <div class="flex items-center justify-center gap-3 py-6 text-p-text-muted" role="status" aria-live="polite">
                    <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                    <span class="text-sm">{{ 'bookingV2.person.search.loading' | translate }}</span>
                </div>
            }

            @if (personShowNoResults()) {
                <div class="text-center py-6 text-p-text-muted">
                    <span class="material-icons text-3xl opacity-60 mb-1 block" aria-hidden="true">person_off</span>
                    <p class="text-sm m-0">{{ 'bookingV2.person.search.noResults' | translate }}</p>
                </div>
            }

            @if (!loadingPersonSearch() && personSearchHasResults()) {
                <div
                    class="flex flex-col gap-1.5 max-h-[420px] overflow-y-auto pr-1"
                    role="listbox"
                    [attr.aria-label]="('bookingV2.person.search.title' | translate)">
                    @for (option of personSearchResult(); track option.kind + '-' + option.id) {
                        <button
                            type="button"
                            role="option"
                            [attr.aria-selected]="selectedPerson()?.id === option.id && selectedPerson()?.kind === option.kind"
                            class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                   bg-p-content-bg border-p-border
                                   hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]"
                            (click)="selectExistingPerson(option)">
                            <div class="flex items-start gap-3">
                                <span class="material-icons flex-shrink-0 mt-0.5 text-p-text-muted" aria-hidden="true">
                                    {{ option.kind === 'company' ? 'business' : 'person' }}
                                </span>
                                <div class="flex-1 min-w-0">
                                    <div class="font-semibold text-p-text">{{ option.displayName }}</div>
                                    @if (option.contactPerson) {
                                        <div class="text-sm text-p-text-muted">
                                            {{ 'bookingV2.person.contactPerson' | translate }}: {{ option.contactPerson }}
                                        </div>
                                    }
                                    @if (option.mail || option.phone) {
                                        <div class="text-sm text-p-text-muted mt-0.5">
                                            @if (option.mail) {
                                                {{ option.mail }}@if (option.phone) { · }
                                            }
                                            @if (option.phone) {
                                                {{ option.phone }}
                                            }
                                        </div>
                                    }
                                    @if (option.address) {
                                        <div class="text-xs text-p-text-muted mt-0.5">{{ option.address }}</div>
                                    }
                                </div>
                            </div>
                        </button>
                    }
                </div>
            }
        </section>
    }

    <!-- Quick-create new person/company -->
    @if (personMode() === 'new' && !selectedPerson()) {
        <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

            <div class="flex items-center justify-between gap-3">
                <h3 class="text-base font-semibold text-p-text m-0">
                    {{ newFormTitleKey() | translate }}
                </h3>
                <button
                    type="button"
                    class="text-p-text-muted hover:text-p-text rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                    [attr.aria-label]="('bookingV2.person.new.closeAria' | translate)"
                    (click)="closePersonPanel()">
                    <span class="material-icons" aria-hidden="true">close</span>
                </button>
            </div>

            <u2b-segmented-toggle
                [options]="personTypeOptions"
                [value]="newPersonType()"
                [ariaLabel]="('bookingV2.person.new.typeAria' | translate)"
                (valueChange)="newPersonType.set($event)" />

            @if (newPersonType() === 'person') {
                <div class="flex flex-col gap-1 mb-3">
                    <label class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.person.salutation' | translate }}
                        <span class="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <p-select styleClass="w-full" appendTo="body" optionValue="value"
                        [options]="newPersonSalutationOptions" [ngModel]="newSalutation()" (ngModelChange)="newSalutation.set($event)">
                        <ng-template #selectedItem let-o>@if (o) { {{ o.labelKey | translate }} }</ng-template>
                        <ng-template let-o #item>{{ o.labelKey | translate }}</ng-template>
                        <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                    </p-select>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label for="np-first" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.person.new.firstName' | translate }}
                            <span class="text-p-text-muted font-normal">({{ 'bookingV2.person.new.optional' | translate }})</span>
                        </label>
                        <input
                            pInputText
                            id="np-first"
                            type="text"
                            class="w-full"
                            [placeholder]="('bookingV2.person.new.firstNamePlaceholder' | translate)"
                            [ngModel]="newFirstName()"
                            (ngModelChange)="newFirstName.set($event)" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="np-last" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.person.new.lastName' | translate }}
                            <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <input
                            pInputText
                            id="np-last"
                            type="text"
                            class="w-full"
                            [placeholder]="('bookingV2.person.new.lastNamePlaceholder' | translate)"
                            [ngModel]="newLastName()"
                            (ngModelChange)="newLastName.set($event)" />
                    </div>
                </div>
            } @else {
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label for="np-company" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.person.new.companyName' | translate }}
                            <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <input
                            pInputText
                            id="np-company"
                            type="text"
                            class="w-full"
                            [placeholder]="('bookingV2.person.new.companyNamePlaceholder' | translate)"
                            [ngModel]="newCompanyName()"
                            (ngModelChange)="newCompanyName.set($event)" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="np-contact" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.person.new.contactPerson' | translate }}
                        </label>
                        <input
                            pInputText
                            id="np-contact"
                            type="text"
                            class="w-full"
                            [placeholder]="('bookingV2.person.new.contactPersonPlaceholder' | translate)"
                            [ngModel]="newContactPerson()"
                            (ngModelChange)="newContactPerson.set($event)" />
                    </div>
                </div>
            }

            <div class="flex flex-col gap-1">
                <label for="np-address" class="text-sm font-medium text-p-text">
                    {{ 'bookingV2.person.new.address' | translate }}
                    <span class="text-p-text-muted font-normal">({{ 'bookingV2.person.new.optional' | translate }})</span>
                </label>
                <input
                    pInputText
                    id="np-address"
                    type="text"
                    class="w-full"
                    [placeholder]="('bookingV2.person.new.addressPlaceholder' | translate)"
                    [ngModel]="newAddress()"
                    (ngModelChange)="newAddress.set($event)" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                    <label for="np-phone" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.person.new.phone' | translate }}
                        <span class="text-p-text-muted font-normal">({{ 'bookingV2.person.new.optional' | translate }})</span>
                    </label>
                    <input
                        pInputText
                        id="np-phone"
                        type="tel"
                        class="w-full"
                        [placeholder]="('bookingV2.person.new.phonePlaceholder' | translate)"
                        [ngModel]="newPhone()"
                        (ngModelChange)="newPhone.set($event)" />
                </div>
                <div class="flex flex-col gap-1">
                    <label for="np-mail" class="text-sm font-medium text-p-text flex items-center gap-1">
                        {{ 'bookingV2.person.new.mail' | translate }}
                        <span class="text-p-text-muted font-normal">({{ 'bookingV2.person.new.optional' | translate }})</span>
                        <span
                            class="material-icons text-base text-p-text-muted cursor-help"
                            [pTooltip]="('bookingV2.person.new.mailHelp' | translate)"
                            tooltipPosition="top">help_outline</span>
                    </label>
                    <input
                        pInputText
                        id="np-mail"
                        type="email"
                        class="w-full"
                        [placeholder]="('bookingV2.person.new.mailPlaceholder' | translate)"
                        [ngModel]="newMail()"
                        (ngModelChange)="newMail.set($event)" />
                </div>
            </div>

            <div
                class="flex items-start gap-2 rounded-lg border border-p-border px-3 py-2.5"
                style="background: color-mix(in srgb, #0284c7 6%, transparent);"
                role="note">
                <span class="material-icons text-base flex-shrink-0 mt-0.5 text-sky-600" aria-hidden="true">info</span>
                <p class="text-sm text-p-text m-0">{{ 'bookingV2.person.new.completeLaterHint' | translate }}</p>
            </div>

            <div class="flex justify-end">
                <p-button
                    type="button"
                    [disabled]="!newPersonValid()"
                    (onClick)="saveNewPerson()">
                    <span pButtonIcon class="material-icons" style="font-size: 1.125rem">check</span>
                    <span pButtonLabel>{{ newFormSaveLabelKey() | translate }}</span>
                </p-button>
            </div>
        </section>
    }

    <!-- Selected person -->
    @if (selectedPerson(); as p) {
        <u2b-indicator-card accent="violet" [icon]="p.kind === 'company' ? 'business' : 'person'">
            <div class="flex items-center gap-2 flex-wrap">
                <span class="text-xs font-medium text-p-text-muted uppercase tracking-wide">
                    {{ (p.kind === 'company' ? 'bookingV2.person.assignedCompany' : 'bookingV2.person.assignedPerson') | translate }}
                </span>
                @if (p.origin === 'new') {
                    <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        {{ 'bookingV2.person.newBadge' | translate }}
                    </span>
                }
            </div>
            <div class="font-semibold text-p-text mt-0.5">{{ p.displayName }}</div>
            @if (p.contactPerson) {
                <div class="text-sm text-p-text-muted">
                    {{ 'bookingV2.person.contactPerson' | translate }}: {{ p.contactPerson }}
                </div>
            }
            @if (p.mail || p.phone) {
                <div class="text-sm text-p-text-muted mt-0.5">
                    @if (p.mail) {
                        {{ p.mail }}@if (p.phone) { · }
                    }
                    @if (p.phone) {
                        {{ p.phone }}
                    }
                </div>
            }
            @if (p.address) {
                <div class="text-xs text-p-text-muted mt-0.5">{{ p.address }}</div>
            }
            <u2b-icon-button
                u2bIndicatorAction
                icon="delete_outline"
                [ariaLabel]="('bookingV2.person.removeAria' | translate)"
                [tooltip]="('bookingV2.person.removeHelp' | translate)"
                tooltipPosition="top"
                (clicked)="removePerson()" />
        </u2b-indicator-card>
    }

    <!-- Beziehung -->
    @if (selectedPerson()) {
        <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-4">

            <div class="flex items-center gap-2">
                <h3 class="text-base font-semibold text-p-text m-0">
                    {{ relationKey('title') | translate }}
                </h3>
                <span
                    class="material-icons text-base text-p-text-muted cursor-help"
                    [pTooltip]="(relationKey('help') | translate)"
                    tooltipPosition="top">help_outline</span>
            </div>

            <!-- Selected primary relation — same orange accent card as the date
                 step's summary (primary border + tint + icon). -->
            @if (selectedRelation(); as rel) {
                <u2b-indicator-card accent="primary" icon="sell">
                    <div class="font-semibold text-p-text">{{ rel.name }}</div>
                    @if (rel.note) {
                        <p class="text-sm text-p-text-muted m-0 mt-0.5">{{ rel.note }}</p>
                    }

                    <!-- Read-only preview of the Leistungen attached to this
                         Beziehung (billed as subscriptions on save). -->
                    <div class="mt-2">
                        <div class="flex items-center gap-1.5 text-xs font-medium text-p-text-muted uppercase tracking-wide">
                            <span class="material-icons text-base" aria-hidden="true">timeline</span>
                            {{ 'bookingV2.person.relationProducts.title' | translate }}
                        </div>
                        @if (selectedRelationProducts().length) {
                            <ul class="list-none p-0 m-0 mt-1 flex flex-col gap-1">
                                @for (p of selectedRelationProducts(); track p.productId) {
                                    <li class="flex flex-col gap-1">
                                        <div class="flex items-baseline justify-between gap-3 text-sm text-p-text">
                                            <span class="flex items-baseline gap-1.5 min-w-0">
                                                <span class="truncate">{{ relationLineTitle(p) }}</span>
                                                @if (!isNotBilled(p)) {
                                                    <span class="text-p-text-muted">× {{ fmtQty(relationQuantity(p)) }}</span>
                                                    @if (p.unitLabelKey) { <span class="text-p-text-muted">{{ p.unitLabelKey | unitTypesField }}</span> }
                                                }
                                                @if (p.hasDynamicPrice) {
                                                    <span class="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full inline-flex items-center gap-0.5 flex-shrink-0">
                                                        <span class="material-icons text-[10px] leading-none" aria-hidden="true">auto_awesome</span>
                                                        {{ 'bookingV2.dynamicPriceBreakdown.badge' | translate }}
                                                    </span>
                                                }
                                                @if (hasRelationLineOverride(p)) {
                                                    <span class="px-1.5 py-0.5 bg-sky-100 text-sky-800 text-xs rounded-full flex-shrink-0">
                                                        {{ 'bookingV2.person.relationProducts.manualBadge' | translate }}
                                                    </span>
                                                }
                                            </span>
                                            <span class="flex items-center gap-1 flex-shrink-0">
                                                @if (isNotBilled(p)) {
                                                    <span class="text-xs text-p-text-muted">{{ 'dialogs.booking.tenant.productRuleNotApplied' | translate }}</span>
                                                } @else {
                                                    <span class="tabular-nums">{{ fmt(grossOf(relationQuantity(p), relationLineUnitPrice(p), p.vatValue)) }} €</span>
                                                    @if (canEditRelationLines() && editingRelationProductId() !== p.productId) {
                                                        <u2b-icon-button
                                                            icon="edit"
                                                            [ariaLabel]="('bookingV2.person.relationProducts.editAria' | translate)"
                                                            [tooltip]="('bookingV2.person.relationProducts.editAria' | translate)"
                                                            tooltipPosition="top"
                                                            (clicked)="openRelationLineEditor(p)" />
                                                    }
                                                }
                                            </span>
                                        </div>

                                        <!-- The rule names other Beziehungen than this booking's — pick
                                             the one to price with (Bierdeckel: "Dynamischer Preis"). -->
                                        @if (pricingRelationChoices().get(p.productId); as choices) {
                                            <div class="mt-1">
                                                <label class="block text-xs font-medium text-p-text-muted uppercase tracking-wide mb-1">
                                                    {{ 'bookingV2.person.relationProducts.pricingRelation.label' | translate }}
                                                    <span class="text-red-500" aria-hidden="true">*</span>
                                                </label>
                                                <p-select styleClass="w-full" appendTo="body"
                                                    [options]="choices" optionLabel="name" optionValue="id"
                                                    [showClear]="true"
                                                    [ngModel]="pickedPricingRelationId(p)"
                                                    (ngModelChange)="setPricingRelation(p, $event ?? null)"
                                                    [placeholder]="('bookingV2.person.relationProducts.pricingRelation.placeholder' | translate)">
                                                    <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                                                    <ng-template #clearicon><span class="material-icons">close</span></ng-template>
                                                </p-select>
                                                @if (pickedPricingRelationId(p) === null) {
                                                    <p class="text-xs text-red-600 m-0 mt-1 flex items-center gap-1">
                                                        <span class="material-icons text-sm" aria-hidden="true">error_outline</span>
                                                        {{ 'bookingV2.person.relationProducts.pricingRelation.required' | translate }}
                                                    </p>
                                                } @else {
                                                    <p class="text-xs text-p-text-muted m-0 mt-1">{{ 'bookingV2.person.relationProducts.pricingRelation.help' | translate }}</p>
                                                }
                                            </div>
                                        }

                                        @if (editingRelationProductId() === p.productId) {
                                            <div class="mt-1 p-3 rounded-lg border border-p-border flex flex-col gap-3"
                                                 style="background: color-mix(in srgb, var(--p-primary-color) 5%, transparent);">
                                                <div class="grid grid-cols-1 sm:grid-cols-[1fr_10rem] gap-3">
                                                    <div class="flex flex-col gap-1">
                                                        <label class="text-sm font-medium text-p-text">{{ 'bookingV2.person.relationProducts.nameLabel' | translate }}</label>
                                                        <input pInputText class="w-full"
                                                            [ngModel]="editRelationTitle()" (ngModelChange)="editRelationTitle.set($event)" />
                                                    </div>
                                                    <div class="flex flex-col gap-1">
                                                        <label class="text-sm font-medium text-p-text">{{ 'bookingV2.person.relationProducts.priceLabel' | translate }}</label>
                                                        <p-inputnumber [min]="0" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2"
                                                            [locale]="currentLocale" suffix=" €" styleClass="w-full"
                                                            [ngModel]="editRelationPrice()" (ngModelChange)="editRelationPrice.set($event)" />
                                                    </div>
                                                </div>
                                                <div class="flex flex-wrap items-center justify-end gap-2">
                                                    @if (hasRelationLineOverride(p)) {
                                                        <p-button size="small" severity="secondary" [text]="true" (onClick)="resetRelationLine(p)">
                                                            <span pButtonIcon class="material-icons text-base">restart_alt</span>
                                                            <span pButtonLabel>{{ 'bookingV2.person.relationProducts.resetToRule' | translate }}</span>
                                                        </p-button>
                                                    }
                                                    <p-button size="small" severity="secondary" [outlined]="true"
                                                        [label]="('common.cancel' | translate)" (onClick)="closeRelationLineEditor()" />
                                                    <p-button size="small"
                                                        [label]="('common.apply' | translate)" (onClick)="applyRelationLineEdit(p)" />
                                                </div>
                                            </div>
                                        }

                                        <!-- Price composition shown inline beneath the product info
                                             for rule-priced products (no expand needed). -->
                                        @if (!isNotBilled(p) && p.hasDynamicPrice && p.dynamicPrice) {
                                            <booking-v2-dynamic-price-breakdown class="mt-1" [dynamicPrice]="p.dynamicPrice" />
                                        }
                                    </li>
                                }
                            </ul>
                        } @else {
                            <p class="text-sm text-p-text-muted m-0 mt-1">{{ relationProductsEmptyKey() | translate }}</p>
                        }
                        @if (selectedRelationProducts().length) {
                            <p class="text-xs text-p-text-muted m-0 mt-1">{{ 'bookingV2.person.relationProducts.priceHint' | translate }}</p>
                        }
                    </div>

                    <!-- Season picker — only when this RELATION's rules branch on the
                         season. Binds the global selectedSeason (shared with services). -->
                    @if (showSeasonPicker()) {
                        <div class="mt-3">
                            <label class="block text-xs font-medium text-p-text-muted uppercase tracking-wide mb-1">
                                {{ 'bookingV2.person.season.label' | translate }}
                                <span class="text-red-500" aria-hidden="true">*</span>
                            </label>
                            <p-select styleClass="w-full" appendTo="body"
                                [options]="seasons()" optionLabel="name" dataKey="id"
                                [showClear]="true"
                                [ngModel]="selectedSeason()"
                                (ngModelChange)="setSeason($event)"
                                [placeholder]="('bookingV2.person.season.placeholder' | translate)" />
                            @if (seasonMissing()) {
                                <p class="text-xs text-red-600 m-0 mt-1 flex items-center gap-1">
                                    <span class="material-icons text-sm" aria-hidden="true">error_outline</span>
                                    {{ 'bookingV2.person.season.required' | translate }}
                                </p>
                            } @else {
                                <p class="text-xs text-p-text-muted m-0 mt-1">{{ 'bookingV2.person.season.help' | translate }}</p>
                            }
                        </div>
                    }

                    <!-- Lagerplatz picker — only when this RELATION's rules branch on
                         the Lagerplatz. Binds the global selectedWinterStorage. -->
                    @if (showWinterStoragePicker()) {
                        <div class="mt-3">
                            <label class="block text-xs font-medium text-p-text-muted uppercase tracking-wide mb-1">
                                {{ 'bookingV2.person.winterStorage.label' | translate }}
                                <span class="text-red-500" aria-hidden="true">*</span>
                            </label>
                            <p-select styleClass="w-full" appendTo="body"
                                [options]="winterStorages()" optionLabel="handle" dataKey="id"
                                [filter]="true" filterBy="handle" [showClear]="true"
                                [ngModel]="selectedWinterStorage()"
                                (ngModelChange)="setWinterStorage($event)"
                                [placeholder]="('bookingV2.person.winterStorage.placeholder' | translate)">
                                <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                                <ng-template #clearicon><span class="material-icons">close</span></ng-template>
                            </p-select>
                            @if (winterStorageMissing()) {
                                <p class="text-xs text-red-600 m-0 mt-1 flex items-center gap-1">
                                    <span class="material-icons text-sm" aria-hidden="true">error_outline</span>
                                    {{ 'bookingV2.person.winterStorage.required' | translate }}
                                </p>
                            } @else {
                                <p class="text-xs text-p-text-muted m-0 mt-1">{{ 'bookingV2.person.winterStorage.help' | translate }}</p>
                            }
                        </div>
                    }

                    <!-- Hints for RELATION rule entities the wizard can't supply. -->
                    @if (priceWarnings().length) {
                        <div class="mt-3 flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                            @if (priceWarnings().includes('berthMissing')) {
                                <div class="flex items-start gap-2 text-sm text-amber-900">
                                    <span class="material-icons text-base flex-shrink-0" aria-hidden="true">warning</span>
                                    <span>{{ 'bookingV2.person.relation.priceWarning.berthMissing' | translate }}</span>
                                </div>
                            }
                        </div>
                    }

                    <u2b-icon-button
                        u2bIndicatorAction
                        icon="delete_outline"
                        [ariaLabel]="(relationKey('removeAria') | translate)"
                        [tooltip]="(relationKey('removeHelp') | translate)"
                        tooltipPosition="top"
                        (clicked)="clearSelectedRelation()" />
                </u2b-indicator-card>
            }

            <!-- Relation picker (shown until a relation is chosen) -->
            @if (selectedRelation() === null) {
                <div class="flex flex-col gap-3">
                    <p class="text-sm text-p-text-muted m-0">
                        {{ relationKey('choosePrompt') | translate }}
                    </p>
                    <p-iconfield iconPosition="left" styleClass="w-full">
                        <p-inputicon>
                            <span class="material-icons text-p-text-muted">search</span>
                        </p-inputicon>
                        <input
                            pInputText
                            type="search"
                            class="w-full"
                            [ngModel]="relationQuery()"
                            (ngModelChange)="onRelationQueryChange($event)"
                            [placeholder]="(relationKey('searchPlaceholder') | translate)"
                            [attr.aria-label]="(relationKey('searchPlaceholder') | translate)" />
                    </p-iconfield>

                    @if (filteredRelations().length === 0) {
                        <p class="text-sm text-p-text-muted m-0 py-2 text-center">
                            {{ relationKey('noResults') | translate }}
                        </p>
                    } @else {
                        <div
                            class="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto pr-1"
                            role="listbox"
                            [attr.aria-label]="(relationKey('title') | translate)">
                            @for (rel of filteredRelations(); track rel.id) {
                                @let isDuplicate = isRelationDuplicate(rel);
                                <button
                                    type="button"
                                    role="option"
                                    [attr.aria-selected]="isRelationSelected(rel)"
                                    class="w-full p-3 border rounded-lg text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary
                                           bg-p-content-bg border-p-border
                                           hover:border-p-primary hover:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,transparent)]
                                           aria-selected:border-p-primary
                                           aria-selected:bg-[color-mix(in_srgb,var(--p-primary-color)_10%,transparent)]
                                           aria-selected:shadow-[0_1px_3px_color-mix(in_srgb,var(--p-primary-color)_15%,transparent)]"
                                    (click)="selectRelation(rel)">
                                    <div class="flex items-center justify-between gap-3">
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center gap-2">
                                                @if (rel.hexColor) {
                                                    <span class="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" [style.background]="rel.hexColor" aria-hidden="true"></span>
                                                }
                                                <span class="font-semibold text-p-text">{{ rel.name }}</span>
                                                @if (isDuplicate) {
                                                    <span class="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-p-surface-100 text-p-text-muted uppercase tracking-wide">
                                                        {{ 'bookingV2.person.relation.alreadyActiveBadge' | translate }}
                                                    </span>
                                                }
                                            </div>
                                            @if (rel.note) {
                                                <div class="text-sm text-p-text-muted mt-0.5">{{ rel.note }}</div>
                                            }
                                            @if (isDuplicate) {
                                                <div class="text-xs text-p-text-muted mt-1">
                                                    {{ relationKey('alreadyActiveHint') | translate: { name: selectedPerson()?.displayName } }}
                                                </div>
                                            }
                                        </div>
                                        <span class="material-icons flex-shrink-0" [class.text-p-primary]="isRelationSelected(rel)" [class.text-gray-300]="!isRelationSelected(rel)" aria-hidden="true">
                                            {{ isRelationSelected(rel) ? 'check_box' : 'check_box_outline_blank' }}
                                        </span>
                                    </div>
                                </button>
                            }
                        </div>
                    }
                </div>
            }

        </section>
    }

    <!-- Notes -->
    @if (selectedPerson()) {
        <section class="bg-p-content-bg border border-p-border rounded-lg p-5 flex flex-col gap-1">
            <label for="booking-notes" class="text-sm font-medium text-p-text">
                {{ 'bookingV2.person.notes.label' | translate }}
                <span class="text-p-text-muted font-normal">({{ 'bookingV2.person.new.optional' | translate }})</span>
            </label>
            <textarea
                pTextarea
                id="booking-notes"
                rows="4"
                class="w-full resize-none"
                [maxlength]="notesMaxLength"
                [placeholder]="('bookingV2.person.notes.placeholder' | translate)"
                [ngModel]="bookingNotes()"
                (ngModelChange)="onNotesChange($event)"></textarea>
            <div class="text-right text-xs text-p-text-muted">
                {{ bookingNotes().length }} / {{ notesMaxLength }}
            </div>
        </section>
    }

</div>
